<template>
    <UserTrackingTable v-if="userTracking.length > 0">
        <template #filters>
            <DropdownFilter
                @apply="applyFilters(false)"
                @clear="clearFilters"
                @disable="disableFilter"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.platform_name"
                            v-model:options="filterPlatformOptions"
                            name="platform_name"
                            class="w-full"
                            :placeholder="$t('platform')"
                            :on-search="loadPlatformNameOptions"
                            :disabled="disableFilter('platform_name')"
                        />
                    </label> </DropdownFilterItem
                ><DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.event"
                            name="countries"
                            class="w-full"
                            :options="eventOptions"
                            :disabled="disableFilter('event')"
                            :placeholder="$t('event')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </UserTrackingTable>
    <ViewTrackingInfoModal
        v-if="viewModalOpen"
        v-model:open="viewModalOpen"
        :data="(selectedData as UserTrackingHistory)"
        @update:open="viewModalOpen = $event"
    />
</template>

<script setup lang="ts">
import DropdownFilter from '@src/components/DropdownFilter.vue'
import DropdownFilterItem from '@src/components/DropdownFilterItem.vue'
import SelectField from '@src/components/SelectField.vue'
import { useTable } from '@src/composables/common'
import {
    LegalProfile,
    NaturalProfile,
    Platform,
    TableSorting,
    UserTrackingHistory,
} from '@src/types'
import { getPlatforms } from '@src/utils/api/platforms'
import { getUserTracking } from '@src/utils/api/userTracking'
import { useUserTrackingStore } from '@src/utils/stores/userTracking'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { onMounted, ref, toRefs } from 'vue'
import ViewTrackingInfoModal from './ViewTrackingInfoModal.vue'

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            trackable_id?: string | null
            platform_name?: string | null
        } | null
    }>(),
    {
        initialFilters: null,
    }
)

const userTrackingStore = useUserTrackingStore()

const { eventOptions } = storeToRefs(userTrackingStore)

const viewModalOpen = ref(false)
const selectedData = ref<UserTrackingHistory | null>(null)
const userTracking = ref<UserTrackingHistory[]>([])
const userTrackingCount = ref<number>(0)

const filterPlatformOptions = ref<string[]>([])

const { initialFilters } = toRefs(props)

const {
    TableWrapper: UserTrackingTable,
    sorting,
    page,
    limit,
    filters,
    filtersFields,
    applyFilters,
    clearFilters,
    setFilters,
} = useTable({
    loadData: loadUserTracking,
    filters: {
        trackable_id: null,
        platform_name: null,
        event: null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'user-tracking-table',
        label: 'user_tracking',
        help: 'help_user_tracking_table',
        pagination: true,
        data: userTracking,
        total: userTrackingCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'ID',
                key: 'uuid',
                select: true,
            },
            {
                label: 'ID',
                key: 'uuid',
                url: profileUrl,
            },
            {
                label: 'user',
                key: '',
                cellRenderer: (props: { rowData: UserTrackingHistory }) => {
                    return props.rowData.trackable_type ===
                        'App\\Models\\LegalProfile'
                        ? (props.rowData.trackable as LegalProfile)
                              ?.company_name
                        : (props.rowData.trackable as NaturalProfile)
                              ?.first_name +
                              ' ' +
                              (props.rowData.trackable as NaturalProfile)
                                  ?.last_name
                },
                url: profileUrl,
            },
            {
                label: 'platform_name',
                key: 'platform_name',
            },
            {
                label: 'event',
                key: 'event',
                cellRenderer: (props: { rowData: UserTrackingHistory }) => {
                    return (
                        props?.rowData?.event &&
                        props?.rowData?.event
                            ?.split('_')
                            .map((word: string) => {
                                return (
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                            })
                            .join(' ')
                    )
                },
            },
            {
                label: 'created_at',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: { rowData: UserTrackingHistory }) => {
                    return moment(props.rowData.created_at).format(
                        'DD.MM.YYYY HH:mm'
                    )
                },
            },
        ],
        actions: [
            {
                action: (rowData: UserTrackingHistory) => {
                    selectedData.value = rowData
                    viewModalOpen.value = true
                },
                icon: 'fa-eye',
                title: 'view',
                condition: true,
            },
        ],
    },
})

const disableFilter = (filter: string) => {
    if (initialFilters.value) {
        return (
            Object.keys(initialFilters.value).filter((item) => {
                return item === filter
            }).length > 0
        )
    }
    return false
}

async function loadUserTracking() {
    const userTrackingRequest = await getUserTracking(
        filters.value,
        sorting.value,
        page.value,
        limit.value
    )
    if (
        userTrackingRequest.data.success &&
        userTrackingRequest.status === 206
    ) {
        userTracking.value = userTrackingRequest.data.user_tracking_history
        userTrackingCount.value =
            userTrackingRequest.data.user_tracking_history_count
    }
}

async function loadPlatformNameOptions(search: string) {
    const initialPlatformsRequest = await getPlatforms({
        'filter[platform_name]': search,
    })
    filterPlatformOptions.value = initialPlatformsRequest.data.platforms.map(
        (platform: Platform) => {
            return platform.platform_name as string
        }
    )
}

function profileUrl(userTrackingHistory: UserTrackingHistory) {
    return userTrackingHistory.trackable_type === 'App\\Models\\NaturalProfile'
        ? `/naturalProfile/${userTrackingHistory.trackable_id}`
        : `/legalProfile/${userTrackingHistory.trackable_id}`
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        setFilters()
    }
    await loadUserTracking()
})
</script>
